// Justified gallery layout for the "mosaic" gallery display.
// Lays images out in full-width rows of uniform height (variable per row),
// keeping each image's natural ratio and aligning both left and right edges,
// like the previous site. Recomputed on load, on resize and after "Charger plus".
export const GJ = {
    galleries: [],
    _resizeTimer: null,

    init: () => {
        GJ.galleries = [...document.querySelectorAll('.gallery-masonry')];
        if (!GJ.galleries.length) return;

        GJ.galleries.forEach(gallery => GJ.setup(gallery));
        window.addEventListener('resize', GJ.onResize);
    },

    onResize: () => {
        clearTimeout(GJ._resizeTimer);
        GJ._resizeTimer = setTimeout(() => {
            GJ.galleries.forEach(gallery => GJ.layout(gallery));
        }, 150);
    },

    // Below this width the gallery falls back to a uniform CSS grid (see SCSS),
    // because justified rows of 1-2 images look uneven on phones.
    GRID_BREAKPOINT: 576,

    // Target row height depends on the viewport width.
    targetHeight: () => {
        const w = window.innerWidth;
        if (w < 992) return 200;
        return 260;
    },

    // On desktop, load the currently visible images (forcing eager fetch so their
    // natural size is known) and justify once they are ready. On phones the layout
    // is a uniform square grid that needs no ratios, so images stay lazy.
    setup: (gallery) => {
        if (!gallery || !gallery.classList.contains('gallery-masonry')) return;

        if (window.innerWidth < GJ.GRID_BREAKPOINT) {
            GJ.layout(gallery);
            return;
        }

        const imgs = [...gallery.querySelectorAll('.masonry-item:not(.d-none) img')];
        if (!imgs.length) return;

        let pending = imgs.length;
        const done = () => {
            if (--pending <= 0) GJ.layout(gallery);
        };

        imgs.forEach(img => {
            img.loading = 'eager';
            if (img.complete && img.naturalWidth) {
                done();
            } else {
                img.addEventListener('load', done, { once: true });
                img.addEventListener('error', done, { once: true });
            }
        });
    },

    layout: (gallery) => {
        const items = [...gallery.querySelectorAll('.masonry-item:not(.d-none)')];
        if (!items.length) return;

        // On phones, drop the inline sizing and let the CSS grid render uniform cells.
        if (window.innerWidth < GJ.GRID_BREAKPOINT) {
            items.forEach(item => {
                item.style.width = '';
                item.style.height = '';
            });
            return;
        }

        const containerWidth = gallery.clientWidth;
        const gap = parseFloat(getComputedStyle(gallery).columnGap) || 6;
        const target = GJ.targetHeight();

        let row = [];
        let ratios = [];

        const flush = (isLastRow) => {
            const ratioSum = ratios.reduce((sum, r) => sum + r, 0);
            const totalGap = gap * (row.length - 1);
            let height = (containerWidth - totalGap) / ratioSum;

            // Do not over-stretch a short last row.
            if (isLastRow && height > target * 1.4) height = target;

            row.forEach((item, i) => {
                item.style.width = Math.floor(ratios[i] * height) + 'px';
                item.style.height = Math.round(height) + 'px';
            });

            row = [];
            ratios = [];
        };

        items.forEach(item => {
            const img = item.querySelector('img');
            const ratio = (img && img.naturalWidth && img.naturalHeight)
                ? img.naturalWidth / img.naturalHeight
                : 1.5;

            row.push(item);
            ratios.push(ratio);

            const ratioSum = ratios.reduce((sum, r) => sum + r, 0);
            if (ratioSum * target + gap * (row.length - 1) >= containerWidth) {
                flush(false);
            }
        });

        if (row.length) flush(true);
    }
};
